import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Monitor, Users, ShieldAlert, Loader2 } from 'lucide-react'
import { SectionHeader } from '@/components/erp/primitives'
import { Forbidden } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { useCan } from '@/shared/auth/session'
import { cn } from '@/lib/utils'
import { UsersTab } from './UsersTab'
import { AdminSessionsTab } from './AdminSessionsTab'
import { forgetAllDevices } from './api'
import { confirmCatastrophic, toast } from '@/shared/ui/confirm'

/**
 * User Management — dedicated page with two views.
 *
 * PollSphere's "User Management" menu entry opened a screen with sub-tabs rather
 * than separate routes, so the tab bar here is the same `h-9 / border-b-2 / -mb-px`
 * bar the administration module uses. The active view lives in `?view=` so a link
 * to the sessions console is shareable.
 */
const VIEWS = [
  { id: 'users', label: 'Users', icon: Users },
  { id: 'sessions', label: 'Sessions', icon: Monitor },
] as const

export function UsersPage() {
  const canRead = useCan('user:read')
  const canAdminDevices = useCan('device:admin')
  const [purging, setPurging] = useState(false)
  const [params, setParams] = useSearchParams()
  const [fallback] = useState('users')
  const raw = params.get('view')
  const view = raw && VIEWS.some((v) => v.id === raw) ? raw : fallback

  const setView = (id: string) => {
    const sp = new URLSearchParams(params)
    sp.set('view', id)
    // Filters belong to the view that owns them — carrying a user filter into
    // the session list would send parameters the endpoint rejects.
    for (const k of ['q', 'status', 'scopeLevel', 'clearance', 'roleId',
                     'neverLoggedIn', 'locked', 'sort', 'sq', 'userId', 'ip', 'activeOnly']) {
      sp.delete(k)
    }
    setParams(sp, { replace: true })
  }

  /**
   * The breach response. Detaches EVERY device on the platform.
   *
   * Deliberately not a mass revoke: after a suspected compromise the question is not which
   * handsets to keep trusting but that none of them are trusted, and each must prove itself
   * to a human again. Detaching sends them all back through enrolment, where somebody reads
   * a code to an activator before anything reaches data.
   *
   * The phrase gate is not decoration. This signs out every field worker at once, and a
   * dialog dismissed with the same button people press all day is not a safeguard.
   */
  async function purgeDevices() {
    const ok = await confirmCatastrophic({
      title: 'Forget every device on the platform?',
      html:
        'This deletes <b>every</b> device binding for <b>every</b> user. Each phone signs '
        + 'out immediately and cannot get back in until it enrols again and somebody '
        + 'activates it with a fresh six-digit code.'
        + '<br><br>Use this when you believe devices have been compromised. Field work '
        + 'already captured on those phones is kept and sends once they are activated again.'
        + '<br><br>This cannot be undone.',
      phrase: 'FORGET ALL DEVICES',
      confirmText: 'Forget every device',
    })
    if (!ok) return
    setPurging(true)
    try {
      const res = await forgetAllDevices()
      toast('good', `${res.detached} device binding${res.detached === 1 ? '' : 's'} removed. `
        + 'Every phone must enrol again.')
    } catch (err) {
      toast('bad', explain(err))
    } finally {
      setPurging(false)
    }
  }

  if (!canRead) return <Forbidden what="user management" />

  return (
    <div className="space-y-4">
      <SectionHeader
        title="User Management"
        description="Accounts, geographic scope, clearance, roles and active sessions."
        right={canAdminDevices ? (
          <button
            className="erp-btn text-red-700 dark:text-red-300 border-red-300 dark:border-red-800"
            disabled={purging}
            onClick={() => void purgeDevices()}
          >
            {purging
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <ShieldAlert className="h-3.5 w-3.5" />}
            Forget all devices
          </button>
        ) : undefined}
      />

      <div className="border-b border-border">
        <div className="flex items-center gap-1 overflow-x-auto">
          {VIEWS.map((v) => {
            const Icon = v.icon
            return (
              <button
                key={v.id}
                onClick={() => setView(v.id)}
                className={cn(
                  'h-9 px-4 text-erp-sm border-b-2 -mb-px inline-flex items-center gap-2 whitespace-nowrap',
                  view === v.id
                    ? 'border-chocolate text-chocolate font-semibold'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {v.label}
              </button>
            )
          })}
        </div>
      </div>

      {view === 'users' && <UsersTab />}
      {view === 'sessions' && <AdminSessionsTab />}
    </div>
  )
}
